import { useEffect, useRef } from 'react'
import type Echo from 'laravel-echo'

import { useAuthStore } from '@/stores/authStore'
import { useEchoClient } from './echoClient'

/**
 * Имя события отметки «пройдено» у блока темы.
 *
 * Точка в начале — как у всех остальных подписок проекта: Laravel шлёт
 * `broadcastAs()`-имя, и без точки laravel-echo искал бы полное имя класса.
 */
export const PROGRAM_ELEMENT_COMPLETED_EVENT = '.program.element.completion.changed'

/**
 * Приватный канал ученика с событиями по его копии программы.
 *
 * Канал пользователя, а не канал назначения: список блоков рисуется и на
 * странице урока, и в «Моей программе», и в карточке назначения — номера
 * назначения там под рукой не везде, а свой идентификатор у клиента есть
 * всегда. Рядом уже живут `user.{id}.notifications` и `user.{id}.dashboard`.
 */
const channelNameFor = (userId: number) => `user.${userId}.programs`

/** Полезная нагрузка события: какой блок и в какое состояние переведён. */
export type ProgramElementCompletedPayload = {
  element_id: number
  /** null — учитель снял отметку */
  completed_at: string | null
}

type Listener = (payload: ProgramElementCompletedPayload) => void

/**
 * Нагрузка приходит из сети, а SPA выкатывается отдельно от API: пока события
 * на сервере нет — хук обязан молчать, а не ронять экран на неожиданной форме
 * объекта. Поэтому не доверяем ничему и на любое несовпадение возвращаем null:
 * список просто останется таким, каким его отдал GET.
 */
function normalizeCompleted(raw: unknown): ProgramElementCompletedPayload | null {
  if (raw === null || typeof raw !== 'object') return null

  const source = raw as Record<string, unknown>
  // Сервер кладёт блок вложенным объектом `element` и отдаёт вместе с ним тему
  // и закладку. Плоскую форму оставляем запасным вариантом: она дешевле
  // проверки версии API и спасёт, если нагрузку когда-нибудь упростят.
  const element = (source.element ?? source) as Record<string, unknown>
  const elementId = Number(element.id ?? source.element_id)

  if (!Number.isFinite(elementId) || elementId <= 0) return null

  const completedAt = element.completed_at ?? source.completed_at

  return {
    element_id: elementId,
    completed_at: typeof completedAt === 'string' && completedAt !== '' ? completedAt : null,
  }
}

/**
 * Подписка одна на всё приложение, а слушателей сколько угодно.
 *
 * Список блоков рисуется по разу на тему, то есть на странице их несколько.
 * Если бы каждый список звал `echo.private()`/`echo.leave()` сам, то уход
 * одного (сменилась тема урока, свернулась карточка) уносил бы общий канал —
 * и соседние списки молча переставали получать отметки. Поэтому канал держит
 * модуль, а хук только добавляет и убирает свой колбэк.
 */
const listeners = new Set<Listener>()

let subscription: { echo: Echo<'reverb'>; channelName: string } | null = null

function dispatch(raw: unknown) {
  const payload = normalizeCompleted(raw)

  if (payload === null) return

  // Копия набора: колбэк слушателя может размонтировать соседний список
  for (const listener of [...listeners]) {
    listener(payload)
  }
}

function detach() {
  if (subscription === null) return

  try {
    subscription.echo.leave(subscription.channelName)
  } catch {
    // Клиент мог быть уже отключён выходом из аккаунта — уходить не с чего
  }

  subscription = null
}

function attach(echo: Echo<'reverb'>, channelName: string) {
  if (subscription !== null && subscription.echo === echo && subscription.channelName === channelName) {
    return
  }

  detach()
  echo.private(channelName).listen(PROGRAM_ELEMENT_COMPLETED_EVENT, dispatch)
  subscription = { echo, channelName }
}

/**
 * Учитель отметил блок темы пройденным — ученик видит это сразу.
 *
 * Раньше отметка доезжала только перезагрузкой: ученик на уроке продолжал
 * смотреть в «не пройдено» и в развёрнутый блок, который учитель уже закрыл.
 */
export function useProgramElementRealtime(onCompleted: Listener): void {
  const userId = useAuthStore((state) => state.user?.id ?? null)

  // Клиент — зависимость эффекта: после пересоздания Echo (вход, выход,
  // impersonation) подписка иначе осталась бы на отключённом сокете
  const echo = useEchoClient()

  const onCompletedRef = useRef(onCompleted)
  onCompletedRef.current = onCompleted

  // Стабильный слушатель: пересоздавать его на каждый рендер значило бы
  // переподписывать набор и терять события между удалением и добавлением
  const listenerRef = useRef<Listener>((payload) => onCompletedRef.current(payload))

  useEffect(() => {
    if (userId === null) return

    const listener = listenerRef.current
    listeners.add(listener)
    attach(echo, channelNameFor(userId))

    return () => {
      listeners.delete(listener)

      // Последний ушёл — держать канал не для кого
      if (listeners.size === 0) detach()
    }
  }, [echo, userId])
}
